import { useEffect, useLayoutEffect, useRef } from 'react';
import { useTabs } from '../../context/TabsContext';
import { useDocRestoreCover } from './useDocRestoreCover';

/**
 * Persist / restore `.doc-fullpage__scroll` scrollTop on the owning doc tab
 * as `docScrollById`. Each doc in the trail keeps its own spot — follow a
 * link, go back, or flip to another tab, and you land where you left off.
 */
export function useDocTabSheetScroll(activeDocId, bodyRef) {
  const { activeTabId, tabs, updateTab } = useTabs();
  const ownerTabIdRef = useRef(null);
  const activeDocIdRef = useRef(activeDocId);
  const latestScrollRef = useRef(0);
  const userScrolledRef = useRef(false);
  const restoringRef = useRef(false);
  const tabsRef = useRef(tabs);
  const updateTabRef = useRef(updateTab);
  tabsRef.current = tabs;
  updateTabRef.current = updateTab;
  activeDocIdRef.current = activeDocId;

  const readSaved = (tabId, docId) => {
    if (!tabId || !docId) return null;
    const tab = tabsRef.current.find((t) => t.id === tabId);
    const v = tab?.docScrollById?.[docId];
    return typeof v === 'number' ? v : null;
  };

  const writeSaved = (tabId, docId, scrollTop) => {
    if (!tabId || !docId) return;
    const top = Math.max(0, Math.round(scrollTop));
    const tab = tabsRef.current.find((t) => t.id === tabId);
    const prev = tab?.docScrollById || {};
    if (prev[docId] === top) return;
    tabsRef.current = tabsRef.current.map((t) => (
      t.id === tabId
        ? { ...t, docScrollById: { ...prev, [docId]: top } }
        : t
    ));
    updateTabRef.current(tabId, { docScrollById: { ...prev, [docId]: top } });
  };

  const flushToTab = (tabId, docId, scrollTopOverride) => {
    const resolvedTabId = tabId || ownerTabIdRef.current;
    const resolvedDocId = docId || activeDocIdRef.current;
    if (!resolvedTabId || !resolvedDocId) return;
    const el = bodyRef.current;
    const fromDom = scrollTopOverride == null && el?.isConnected ? el.scrollTop : null;
    const scrollTop =
      scrollTopOverride != null
        ? scrollTopOverride
        : fromDom != null
          ? fromDom
          : latestScrollRef.current;
    if (scrollTop <= 0 && !userScrolledRef.current) {
      const existing = readSaved(resolvedTabId, resolvedDocId);
      if (existing != null && existing > 0) return;
    }
    writeSaved(resolvedTabId, resolvedDocId, scrollTop);
    latestScrollRef.current = scrollTop;
  };

  const restoreKey = `${activeTabId || ''}:${activeDocId || ''}`;
  const peeked = activeDocId
    ? readSaved(activeTabId || ownerTabIdRef.current, activeDocId)
    : null;
  const { holdCover, releaseCover } = useDocRestoreCover(restoreKey, peeked > 0);

  useLayoutEffect(() => {
    if (!activeDocId) return;
    ownerTabIdRef.current = activeTabId || ownerTabIdRef.current;
    userScrolledRef.current = false;
    const saved = readSaved(ownerTabIdRef.current, activeDocId);
    latestScrollRef.current = saved != null ? saved : 0;
    const el = bodyRef.current;
    if (el) {
      restoringRef.current = true;
      el.scrollTop = latestScrollRef.current;
      requestAnimationFrame(() => {
        restoringRef.current = false;
      });
    }
  }, [activeDocId, activeTabId, bodyRef]);

  useLayoutEffect(() => {
    const tabIdAtStart = activeTabId;
    const docIdAtStart = activeDocId;
    return () => {
      flushToTab(tabIdAtStart, docIdAtStart);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabId]);

  useEffect(() => {
    if (!activeDocId) return undefined;

    const docId = activeDocId;
    const tabId = ownerTabIdRef.current || activeTabId;
    let cancelled = false;
    let el = null;
    let timer = null;
    let raf = null;
    const remembered = { current: latestScrollRef.current };

    const onScroll = () => {
      if (!el) return;
      remembered.current = el.scrollTop;
      latestScrollRef.current = el.scrollTop;
      if (restoringRef.current) return;
      userScrolledRef.current = true;
      releaseCover();
      clearTimeout(timer);
      timer = setTimeout(() => {
        writeSaved(tabId, docId, remembered.current);
      }, 50);
    };

    const bind = () => {
      if (cancelled) return;
      el = bodyRef.current;
      if (!el) {
        raf = requestAnimationFrame(bind);
        return;
      }
      remembered.current = el.scrollTop;
      latestScrollRef.current = el.scrollTop;
      el.addEventListener('scroll', onScroll, { passive: true });
    };
    bind();

    return () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
      clearTimeout(timer);
      el?.removeEventListener('scroll', onScroll);
      flushToTab(tabId, docId, remembered.current);
      if (!activeDocIdRef.current) ownerTabIdRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDocId, activeTabId, bodyRef]);

  useEffect(() => {
    if (!activeDocId) return undefined;
    const tabId = ownerTabIdRef.current || activeTabId;
    if (!tabId) return undefined;

    const saved = readSaved(tabId, activeDocId);
    if (saved == null || saved <= 0) return undefined;

    let cancelled = false;
    let settled = false;
    let attempts = 0;
    let raf = null;
    let ro = null;

    const markReady = () => {
      if (settled) return;
      settled = true;
      releaseCover();
    };

    const apply = (el) => {
      if (userScrolledRef.current) {
        markReady();
        return true;
      }
      const max = Math.max(0, el.scrollHeight - el.clientHeight);
      const target = Math.min(saved, max);
      restoringRef.current = true;
      el.scrollTop = target;
      latestScrollRef.current = target;
      requestAnimationFrame(() => {
        restoringRef.current = false;
      });
      return max >= saved;
    };

    const tryRestore = () => {
      if (cancelled || settled) return;
      const el = bodyRef.current;
      if (!el) {
        if (attempts++ < 80) raf = requestAnimationFrame(tryRestore);
        return;
      }

      if (!ro && typeof ResizeObserver !== 'undefined') {
        ro = new ResizeObserver(() => {
          if (cancelled || settled) return;
          const current = bodyRef.current;
          if (!current) return;
          if (apply(current)) markReady();
        });
        ro.observe(el);
      }

      const max = Math.max(0, el.scrollHeight - el.clientHeight);
      if (max < saved && attempts++ < 120) {
        raf = requestAnimationFrame(tryRestore);
        return;
      }

      if (apply(el)) markReady();
      else if (attempts++ < 120) raf = requestAnimationFrame(tryRestore);
      else markReady();
    };

    raf = requestAnimationFrame(() => {
      raf = requestAnimationFrame(tryRestore);
    });
    const safety = setTimeout(markReady, 2500);

    return () => {
      cancelled = true;
      clearTimeout(safety);
      if (raf) cancelAnimationFrame(raf);
      ro?.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDocId, activeTabId, bodyRef, releaseCover]);

  return holdCover;
}

export default useDocTabSheetScroll;
